import { Implements, Service, ServiceLifetime } from '@gitlab/needle';
import { Logger } from '@gitlab-org/logging';
import { WorkflowRunner } from '@gitlab-lsp/workflow-api';
import { ConfigService } from '@gitlab-org/config';
import { ProjectService } from '@gitlab-org/core';
import { SystemContextManager } from '@gitlab-org/ai-context';
import { McpManagerWorkflowExecutorAdaptor } from '@gitlab-org/ai-configuration';
import { DuoAgentPlatformTracker } from '@gitlab-org/telemetry';
import { BackendFactory } from '../backend_factory';
import { ParsedCliInput } from '../../parse';
import { SessionDataService } from '../../sessions/session_data_service';
import { ToolInputFormatterService } from '../tool_input_formatter';
import { PresentationPolicy } from '../presentation_policy';
import { GitLabParsedOptions } from './gitlab_parsed_options';
import { WorkflowEventMapperService } from './workflow_event_mapper';
import { GitLabBackend } from './gitlab_backend';
import { GitLabModelManager } from './gitlab_model_manager';
import { RootNamespaceIdService } from './root_namespace_id_service';
import { RunCondition, resolveAutoApprove } from './run_condition';
import { createWorkflowStrategy } from './workflow_strategy';

@Implements(BackendFactory)
@Service({
  dependencies: [
    Logger,
    WorkflowRunner,
    ConfigService,
    ParsedCliInput,
    GitLabParsedOptions,
    ProjectService,
    RootNamespaceIdService,
    ToolInputFormatterService,
    SystemContextManager,
    McpManagerWorkflowExecutorAdaptor,
    GitLabModelManager,
    DuoAgentPlatformTracker,
    SessionDataService,
    RunCondition,
    PresentationPolicy,
  ],
  lifetime: ServiceLifetime.Singleton,
})
export class GitLabBackendFactory implements BackendFactory {
  #logger: Logger;

  #workflowRunner: WorkflowRunner;

  #configService: ConfigService;

  #cliInput: ParsedCliInput;

  #backendOpts: GitLabParsedOptions;

  #projectService: ProjectService;

  #rootNamespaceIdService: RootNamespaceIdService;

  #toolInputFormatter: ToolInputFormatterService;

  #systemContextManager: SystemContextManager;

  #mcpManager: McpManagerWorkflowExecutorAdaptor;

  #modelManager: GitLabModelManager;

  #duoAgentPlatformTracker: DuoAgentPlatformTracker;

  #sessionDataService: SessionDataService;

  #runCondition: RunCondition;

  #policy: PresentationPolicy;

  // Auto-approval belongs to neither the source nor the presentation axis. It is
  // resolved once here from the startup RunCondition + the CLI opt-out flag
  // (a bare boolean can't be a DI token: needle drops falsy resolved values).
  #autoApprove: boolean;

  constructor(
    logger: Logger,
    workflowRunner: WorkflowRunner,
    configService: ConfigService,
    cliInput: ParsedCliInput,
    backendOpts: GitLabParsedOptions,
    projectService: ProjectService,
    rootNamespaceIdService: RootNamespaceIdService,
    toolInputFormatter: ToolInputFormatterService,
    systemContextManager: SystemContextManager,
    mcpManager: McpManagerWorkflowExecutorAdaptor,
    modelManager: GitLabModelManager,
    duoAgentPlatformTracker: DuoAgentPlatformTracker,
    sessionDataService: SessionDataService,
    runCondition: RunCondition,
    policy: PresentationPolicy,
  ) {
    this.#logger = logger;
    this.#workflowRunner = workflowRunner;
    this.#configService = configService;
    this.#cliInput = cliInput;
    this.#backendOpts = backendOpts;
    this.#projectService = projectService;
    this.#rootNamespaceIdService = rootNamespaceIdService;
    this.#toolInputFormatter = toolInputFormatter;
    this.#systemContextManager = systemContextManager;
    this.#mcpManager = mcpManager;
    this.#modelManager = modelManager;
    this.#duoAgentPlatformTracker = duoAgentPlatformTracker;
    this.#sessionDataService = sessionDataService;
    this.#runCondition = runCondition;
    this.#policy = policy;
    this.#autoApprove = resolveAutoApprove(runCondition, cliInput.dangerouslySkipPermissions);
  }

  create(existingSessionId?: string): GitLabBackend {
    const workflowEventMapper = new WorkflowEventMapperService(
      this.#toolInputFormatter,
      this.#logger,
    );

    // The startup RunCondition fixes the source/presentation (rails vs local,
    // headless vs interactive). rails never switches sessions, so it is used
    // verbatim. For local, the resume id is per-create: startup
    // `--existing-session-id`, runtime session switch, or none for a fresh
    // session (`/new`).
    const runCondition: RunCondition =
      this.#runCondition.source === 'rails'
        ? this.#runCondition
        : {
            source: 'local',
            presentation: this.#runCondition.presentation,
            resumeWorkflowId: existingSessionId,
          };

    return new GitLabBackend(
      this.#logger,
      this.#workflowRunner,
      this.#configService,
      this.#cliInput,
      this.#backendOpts,
      this.#projectService,
      this.#rootNamespaceIdService,
      workflowEventMapper,
      this.#systemContextManager,
      this.#mcpManager,
      this.#modelManager,
      this.#duoAgentPlatformTracker,
      this.#sessionDataService,
      runCondition,
      createWorkflowStrategy(runCondition, this.#backendOpts),
      this.#policy,
      this.#autoApprove,
    );
  }
}
